import { useId, useState } from "react"
import { AlertCircle, AlertTriangle, ArrowRight, Check, Pencil, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { displayCitationPage } from "@/lib/citation-preview"
import { cn } from "@/lib/utils"
import {
  NEEDS_VALUE_HINT,
  effectiveFillValue,
  evidenceNumber,
  evidenceString,
  fillSourceLabel,
  fillTargetLabel,
  needsUserValue,
} from "@/lib/fill-source"
import type { FillDecision, FillProposal } from "@/types/schedule"

// ===================
// Source pill
// ===================

export function SourcePill({ proposal }: { proposal: FillProposal }) {
  const isUnanswered = proposal.source === "needs_input"
  return (
    <Badge
      variant={isUnanswered ? "warning" : "secondary"}
      dot={isUnanswered}
      className="max-w-full truncate text-[10px]"
    >
      {fillSourceLabel(proposal)}
    </Badge>
  )
}

// ===================
// Popover body
// ===================

export interface ProposalPopoverProps {
  proposal: FillProposal
  /** Formatted current cell value, or an em dash when the cell is empty. */
  currentDisplay: string
  onDecide: (decision: FillDecision, editedValue?: string) => void
  onClose: () => void
  /** Overrides the default target reference (`Sheet!B5` for a cell). */
  targetLabel?: string
}

export function ProposalPopover({
  proposal,
  currentDisplay,
  onDecide,
  onClose,
  targetLabel,
}: ProposalPopoverProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(() => effectiveFillValue(proposal))
  const inputId = useId()

  const isApplied = proposal.appliedAt !== null
  const isUnanswered = needsUserValue(proposal)
  const quote = evidenceString(proposal, "quote")
  const page = displayCitationPage(
    evidenceNumber(proposal, "page") ?? evidenceNumber(proposal, "pageNumber")
  )
  const confidence = proposal.confidence === null ? null : Math.round(proposal.confidence * 100)

  const startEditing = () => {
    setDraft(effectiveFillValue(proposal))
    setIsEditing(true)
  }

  return (
    <div className="space-y-2.5">
      {/* Cell reference + close */}
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 truncate font-mono text-meta">
          {targetLabel ?? fillTargetLabel(proposal)}
        </p>
        <Button
          variant="ghost"
          size="icon"
          className="-mr-1 -mt-1 h-6 w-6 shrink-0"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Current → proposed */}
      <div className="flex flex-wrap items-baseline gap-1.5 text-sm">
        <s data-testid="current-value" className="text-muted-foreground">
          {currentDisplay}
        </s>
        <ArrowRight className="h-3 w-3 shrink-0 self-center text-muted-foreground" />
        {isEditing ? (
          <div className="w-full space-y-1.5 pt-1">
            <Label htmlFor={inputId} className="text-meta">
              Value
            </Label>
            <Input
              id={inputId}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="h-8 bg-muted text-sm"
              autoFocus
            />
          </div>
        ) : (
          <span
            data-testid="proposed-value"
            className="font-medium text-[hsl(var(--ai-accent-text))]"
          >
            {effectiveFillValue(proposal)}
          </span>
        )}
      </div>

      {/* Provenance */}
      <div className="flex flex-wrap items-center gap-2">
        <SourcePill proposal={proposal} />
        {confidence !== null && (
          <span className="text-[11px] text-muted-foreground">{confidence}% confident</span>
        )}
      </div>

      {proposal.rationale && (
        <p className="text-xs leading-relaxed text-muted-foreground">{proposal.rationale}</p>
      )}

      {quote && (
        <div className="rounded-r-md border-l-2 border-[hsl(var(--brand))] bg-muted/30 px-3 py-2">
          <p className="line-clamp-3 text-xs italic text-muted-foreground">“{quote}”</p>
          {page !== null && (
            <p className="mt-1 text-[11px] text-muted-foreground">p.{page}</p>
          )}
        </div>
      )}

      {proposal.warning && (
        <p className="flex items-start gap-1.5 text-xs text-[hsl(var(--status-warning))]">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          {proposal.warning}
        </p>
      )}

      {proposal.applyError && (
        <p className="flex items-start gap-1.5 rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {proposal.applyError}
        </p>
      )}

      {/* Decisions — ghost throughout: the page's one filled primary is "Apply & download" */}
      {!isApplied && (
        <div className={cn("flex items-center gap-1 pt-0.5", isEditing && "justify-end")}>
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={draft.trim().length === 0}
                onClick={() => onDecide("accepted", draft)}
              >
                <Check className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
            </>
          ) : (
            <>
              {/* Wrapper carries the hint: a disabled button is not a hover target. */}
              <span title={isUnanswered ? NEEDS_VALUE_HINT : undefined}>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isUnanswered}
                  onClick={() => onDecide("accepted")}
                >
                  <Check className="mr-1.5 h-3.5 w-3.5" />
                  Accept
                </Button>
              </span>
              <Button variant="ghost" size="sm" onClick={startEditing}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-muted-foreground"
                onClick={() => onDecide("dismissed")}
              >
                Dismiss
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
